import { useState } from "react";
import { Tooltip } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ProductItemWrapper, Wrapper } from "./ProductStyled";
import { setProduct } from "../../../../store/product/product.Slice";
import { useAppDispatch } from "../../../../store/store";

interface Props {
  products: {
    description: string;
    id: string;
    modelNo: string;
    name: string;
    serialNo: string;
    productCategory: {
      name: string;
    };
    productMappedBrand: {
      name: string;
    };
    thumbnailUrl: string | null;
  };
  badge?: "new" | "hot" | "featured" | "bestseller";
  index?: number;
}

const badgeLabels: Record<string, string> = {
  new: "New",
  hot: "Hot",
  featured: "Featured",
  bestseller: "Best Seller",
};

const autoBadges = ["new", "hot", "featured", "bestseller"];

export const ProductItem = ({ products, badge, index = 0 }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const onProductClick = (e: React.MouseEvent, productName: string = "") => {
    e.preventDefault();
    dispatch(setProduct(productName));

    // If already on home page, just scroll to contactUs
    if (location.pathname === "/") {
      const element = document.getElementById("contactUs");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // Navigate to home page with hash
      navigate("/#contactUs");
    }
  };

  // Auto-assign badges based on index if not provided
  const displayBadge = badge || (index < 4 ? autoBadges[index % autoBadges.length] : undefined);

  const hasValidImage = products?.thumbnailUrl && !imageError;

  return (
    <Wrapper>
      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
      <a href="#contactUs" onClick={(e) => onProductClick(e, products?.name)}></a>
      <ProductItemWrapper className={displayBadge ? `has-badge badge-${displayBadge}` : ""}>
        {displayBadge && (
          <span className={`product-badge ${displayBadge}`}>
            {badgeLabels[displayBadge]}
          </span>
        )}
        <div className={`imageWrapper ${!imageLoaded && hasValidImage ? "loading" : ""}`}>
          {hasValidImage ? (
            <>
              <img
                src={products.thumbnailUrl!}
                alt={products?.name || "Product"}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
              <div className="image-overlay" />
            </>
          ) : (
            <div className="no-image">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              <span>No image</span>
            </div>
          )}
        </div>
        <div className="productDetail">
          <span className="categoryTag">
            {products?.productMappedBrand?.name || products?.productCategory?.name}
          </span>
          <h1>{products?.name}</h1>
          {products?.modelNo && (
            <span className="modelNo">Model: {products.modelNo}</span>
          )}
          <Tooltip title={products?.description || ""} arrow placement="top">
            <p>{products?.description}</p>
          </Tooltip>
          <span className="viewBtn">
            Enquire Now
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </ProductItemWrapper>
    </Wrapper>
  );
};
